/**
 * Add Field Picker (RPRT-3080) — slideout that opens from `QueryDetailTakeover`
 * when the user clicks "Insert fields". Each row has its own Insert button;
 * clicking it synthesizes a `QueryFieldRow` via `pickerMapper` and calls
 * `onAddField`. The picker stays open for multiple insertions.
 *
 * `typeHierarchy` and `formulas` come from the parent `DataPackageSlideout`
 * props. Primary fields are fetched through an AG Grid server-side datasource.
 * Dynamic fields are a static catalogue of at-run `DateParameter` tokens.
 *
 * Clicking Insert flips the button to "Inserted" for ~1.5 s then reverts,
 * acting as both confirmation feedback and a double-tap guard.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AgGrid,
  Button,
  Checkbox,
  DropdownButton,
  Flex,
  Grid,
  OverlayStyle,
  SlideoutDrawerV2,
  Text,
  TextInput,
} from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { useDebouncedValue } from "~/common/hooks/useDebouncedValue";
import { HighlightText } from "~/pages/Reporting/components/HighlightText";
import { makeHighlightRenderer } from "~/pages/Reporting/components/HighlightText/makeHighlightRenderer";
import { useAgGridDefaults } from "~/pages/UserAndRoles/utils/gridConfig";
import { SPACING } from "~/styles/variables";
import { DYNAMIC_DATE_PICKER_OPTIONS } from "../../../../shared/DatePeriodSection/constants";
import { getDefaultRollupLevelValue } from "../../../../shared/levels/levelDisplay";
import { toSentenceCaseLabel } from "../../../../shared/sentenceCaseLabel";
import { filterTreeRowsWithAncestors, toTreeRowId } from "../../HierarchyTreeGrid";
import { toCanonicalPrimaryField } from "../../primaryFieldOptions";
import {
  PRIMARY_FIELDS_PAGE_SIZE,
  usePrimaryFieldsDatasource,
  usePrimaryFieldsGridAttachment,
} from "../../usePrimaryFieldsDatasource";
import { synthesizeQueryFieldRow } from "./pickerMapper";
import * as styles from "./styles_vanilla.css";
import { Z_INDEX } from "./zIndex";
import type { Selection, SelectionKey } from "./types";
import type { DateParameter } from "../../../../shared/DatePeriodSection/types";
import type { QueryRollupLevelOption } from "../../../../shared/levels/types";
import type { Formula } from "../../FormulasGrid";
import type { HierarchyNodeData, TypeHierarchy } from "../../HierarchyTreeGrid";
import type { PrimaryFieldItem } from "../../usePrimaryFieldsDatasource";
import type { QueryFieldRow } from "../fieldsMapper";

type AddFieldPickerSlideoutProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Arena scope. `null` for system-level primary fields. */
  arenaId: number | null;
  /** Type hierarchy blob from `finpubDataPackage.typeHierarchy`. */
  typeHierarchy: TypeHierarchy | null;
  /** Active + archived formulas; the picker shows active only. */
  formulas: Formula[];
  /** Sort order for the newly-added row. */
  nextSortOrder: number;
  /** Called with the synthesized row each time the user clicks Insert. */
  onAddField: (row: QueryFieldRow) => void;
  rollupLevelOptions: ReadonlyArray<QueryRollupLevelOption>;
};

/** How long the "Inserted" confirmation stays on the button before it
 *  reverts to "Insert". Doubles as the click-debounce window. */
const ADDED_FLASH_MS = 1500;

type FilterType = "hierarchy" | "formulas" | "dynamicFields" | "primaryFields";

const FILTER_OPTIONS: { value: FilterType; label: string }[] = [
  { value: "hierarchy", label: "Hierarchy nodes" },
  { value: "formulas", label: "Formulas" },
  { value: "dynamicFields", label: "Dynamic fields" },
  { value: "primaryFields", label: "Primary fields" },
];

type DynamicDateRow = {
  id: string;
  key: SelectionKey;
  name: string;
  dynamicDate: DateParameter;
  type: string;
};

const ALL_DYNAMIC_DATE_ROWS: ReadonlyArray<DynamicDateRow> = DYNAMIC_DATE_PICKER_OPTIONS.map(
  (option) => ({
    id: option.value,
    key: `dynamicDate:${option.value}` as SelectionKey,
    name: option.label,
    dynamicDate: option.value,
    type: "Date",
  }),
);

type FlattenedHierarchyNode = {
  id: string;
  key: SelectionKey;
  name: string;
  code: string;
  description?: string;
  path: string[];
  typeHierarchyNodeId: number | null;
};

type PrimaryFieldRow = {
  id: string;
  key: SelectionKey;
  /** `QueryAttributeType.logicalName` — user-facing label. */
  name: string;
  /** Canonical `<table>.<column>` identifier used by `QueryFieldInput.primaryField`. */
  primaryField: string;
  tableName: string | null;
  type: string;
};

/** Grids use autoHeight so the slideout scrolls as one unit.
 *  `suppressHorizontalScroll` removes the scrollbar gutter AG Grid
 *  reserves under autoHeight grids — without it, three stacked grids
 *  contribute ~45px of trailing whitespace inside the slideout. */
const FIELD_GRID_OVERRIDES = {
  domLayout: "autoHeight" as const,
  rowHeight: 50,
  suppressContextMenu: true,
  suppressCellFocus: true,
  suppressHorizontalScroll: true,
  pagination: false,
  sideBar: false,
  statusBar: undefined,
};

/** Flatten the type-hierarchy tree into AG Grid tree-data rows.
 *  The synthetic root is dropped from the path to match how
 *  `QueryFieldFieldsFragment.levels.levels` represents hierarchy paths. */
const flattenHierarchy = (
  node: HierarchyNodeData,
  parentPath: string[] = [],
  isRoot = true,
): FlattenedHierarchyNode[] => {
  const path = isRoot ? parentPath : [...parentPath, node.name];
  const current: FlattenedHierarchyNode | null = isRoot
    ? null
    : {
        id: toTreeRowId(path),
        key: `node:${path.join("/")}`,
        name: node.name,
        code: node.code,
        description: node.description,
        path,
        typeHierarchyNodeId: node.id ?? null,
      };
  const children =
    node.children?.flatMap((child) => flattenHierarchy(child, path, false)) ?? [];
  return current ? [current, ...children] : children;
};

const nodeMatchesSearch = (node: FlattenedHierarchyNode, normalizedSearch: string): boolean =>
  node.name.toLowerCase().includes(normalizedSearch) ||
  node.code.toLowerCase().includes(normalizedSearch);

const formulaMatchesSearch = (formula: Formula, needle: string): boolean => {
  return (
    formula.name.toLowerCase().includes(needle) ||
    formula.description.toLowerCase().includes(needle) ||
    formula.formula.toLowerCase().includes(needle)
  );
};

export { toCanonicalPrimaryField };

const computeFilterLabel = (active: FilterType[]): string => {
  if (active.length === 0 || active.length === FILTER_OPTIONS.length) return "All field types";
  if (active.length === 1) {
    const only = FILTER_OPTIONS.find((o) => o.value === active[0]);
    return `${only?.label ?? active[0]} only`;
  }
  return `${active.length} selected`;
};

type InsertCellRendererProps = {
  isAdded: boolean;
  onInsertClick: () => void;
  testId: string;
};

const InsertCellRenderer = ({ isAdded, onInsertClick, testId }: InsertCellRendererProps) => {
  if (isAdded) {
    return (
      <Button
        variant="link"
        className={styles.addedButton}
        icon="check"
        iconPosition="right"
        disabled
        data-testid={`${testId}-added`}
      >
        Inserted
      </Button>
    );
  }
  return (
    <Button
      variant="link"
      onClick={onInsertClick}
      className={styles.insertButton}
      icon="plus"
      data-testid={`${testId}-insert`}
    >
      Insert
    </Button>
  );
};

type InsertContext<TRow> = {
  disabledKeys: ReadonlySet<string>;
  onInsert: (row: TRow) => void;
  rowKey: (row: TRow) => string;
  testIdPrefix: string;
  /** Passed through context so renderers can highlight matches without
   *  re-creating column defs on every keystroke. */
  searchValue: string;
  /** When provided, hides Insert for rows that aren't valid targets. */
  canInsert?: (row: TRow) => boolean;
};

const insertColumnDef = <TRow,>(): AgGrid.ColDef<TRow> => ({
  colId: "insert",
  headerName: "",
  width: 110,
  sortable: false,
  suppressHeaderMenuButton: true,
  cellClass: styles.insertColumnCell,
  cellRenderer: (params: AgGrid.ICellRendererParams<TRow>) => {
    if (!params.data) return null;
    const ctx = params.context as InsertContext<TRow>;
    if (ctx.canInsert && !ctx.canInsert(params.data)) return null;
    const key = ctx.rowKey(params.data);
    return (
      <InsertCellRenderer
        isAdded={ctx.disabledKeys.has(key)}
        onInsertClick={() => ctx.onInsert(params.data!)}
        testId={`${ctx.testIdPrefix}-${key}`}
      />
    );
  },
});

const HIERARCHY_COLUMN_DEFS: AgGrid.ColDef<FlattenedHierarchyNode>[] = [
  {
    colId: "description",
    headerName: "Description",
    sortable: false,
    flex: 1,
    cellRenderer: makeHighlightRenderer<FlattenedHierarchyNode>((row) => row.description, {
      placeholder: "-",
    }),
  },
  insertColumnDef<FlattenedHierarchyNode>(),
];

const FORMULA_COLUMN_DEFS: AgGrid.ColDef<Formula>[] = [
  {
    colId: "name",
    headerName: "Formula name",
    sortable: false,
    flex: 1,
    cellRenderer: makeHighlightRenderer<Formula>((row) => row.name),
  },
  {
    colId: "description",
    headerName: "Description",
    sortable: false,
    flex: 1,
    cellRenderer: makeHighlightRenderer<Formula>((row) => row.description),
  },
  {
    colId: "formula",
    headerName: "Formula",
    sortable: false,
    flex: 1,
    wrapText: true,
    autoHeight: true,
    cellRenderer: makeHighlightRenderer<Formula>((row) => row.formula),
  },
  insertColumnDef<Formula>(),
];

const PRIMARY_FIELD_COLUMN_DEFS: AgGrid.ColDef<PrimaryFieldRow>[] = [
  {
    colId: "name",
    headerName: "Field name",
    sortable: false,
    flex: 1,
    cellRenderer: makeHighlightRenderer<PrimaryFieldRow>((row) =>
      toSentenceCaseLabel(row.name),
    ),
  },
  {
    colId: "primaryField",
    headerName: "Field key",
    sortable: false,
    flex: 1,
    cellRenderer: makeHighlightRenderer<PrimaryFieldRow>((row) =>
      row.tableName ? toSentenceCaseLabel(row.tableName) : "",
    ),
  },
  { colId: "type", field: "type", headerName: "Type", sortable: false, flex: 1 },
  insertColumnDef<PrimaryFieldRow>(),
];

const DYNAMIC_DATE_COLUMN_DEFS: AgGrid.ColDef<DynamicDateRow>[] = [
  {
    colId: "name",
    headerName: "Field name",
    sortable: false,
    flex: 1,
    cellRenderer: makeHighlightRenderer<DynamicDateRow>((row) => row.name),
  },
  { colId: "type", field: "type", headerName: "Type", sortable: false, flex: 1 },
  insertColumnDef<DynamicDateRow>(),
];

const toPrimaryFieldRow = (field: PrimaryFieldItem): PrimaryFieldRow => {
  const primaryField = toCanonicalPrimaryField(field.name, field.tableName);
  return {
    id: primaryField,
    key: `primary:${primaryField}` as SelectionKey,
    name: field.displayName || field.name,
    primaryField,
    tableName: field.tableName,
    type: field.type,
  };
};

/** Stateful body — mounted only while open so state resets on close via unmount. */
type AddFieldPickerContentProps = Omit<AddFieldPickerSlideoutProps, "isOpen">;

const AddFieldPickerContent = ({
  arenaId,
  typeHierarchy,
  formulas,
  nextSortOrder,
  onAddField,
  rollupLevelOptions,
}: Omit<AddFieldPickerContentProps, "onClose">) => {
  const [searchValue, setSearchValue] = useState("");
  const [activeFilters, setActiveFilters] = useState<FilterType[]>([]);
  const debouncedSearch = useDebouncedValue(searchValue, 150);
  const trimmedSearch = debouncedSearch.trim();
  const needle = trimmedSearch.toLowerCase();
  const primaryFieldSearch = trimmedSearch || null;

  const { datasource: primaryFieldsDatasource, loadState: primaryFieldsLoadState } =
    usePrimaryFieldsDatasource({
      arenaId,
      search: primaryFieldSearch,
      toRow: toPrimaryFieldRow,
    });

  /** Rows currently showing "Inserted". State drives re-renders; timer
   *  handles live in a ref to avoid triggering extra re-renders. */
  const [recentlyAdded, setRecentlyAdded] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );
  const flashTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    const timers = flashTimersRef.current;
    return () => {
      timers.forEach((handle) => clearTimeout(handle));
      timers.clear();
    };
  }, []);

  const flashAdded = useCallback((key: string) => {
    const existing = flashTimersRef.current.get(key);
    if (existing) clearTimeout(existing);
    setRecentlyAdded((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    const handle = setTimeout(() => {
      flashTimersRef.current.delete(key);
      setRecentlyAdded((prev) => {
        if (!prev.has(key)) return prev;
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }, ADDED_FLASH_MS);
    flashTimersRef.current.set(key, handle);
  }, []);

  const allHierarchyNodes = useMemo<FlattenedHierarchyNode[]>(() => {
    const root = typeHierarchy?.tree_structure?.root;
    if (!root) return [];
    return flattenHierarchy(root);
  }, [typeHierarchy]);

  const activeFormulas = useMemo(() => formulas.filter((f) => f.isActive), [formulas]);

  const filteredHierarchy = useMemo(
    () => filterTreeRowsWithAncestors(allHierarchyNodes, needle, nodeMatchesSearch),
    [allHierarchyNodes, needle],
  );

  const filteredFormulas = useMemo(() => {
    if (!needle) return activeFormulas;
    return activeFormulas.filter((f) => formulaMatchesSearch(f, needle));
  }, [activeFormulas, needle]);

  const filteredDynamicDates = useMemo(() => {
    if (!needle) return ALL_DYNAMIC_DATE_ROWS;
    return ALL_DYNAMIC_DATE_ROWS.filter((row) => row.name.toLowerCase().includes(needle));
  }, [needle]);

  const showSection = useCallback(
    (filter: FilterType) => activeFilters.length === 0 || activeFilters.includes(filter),
    [activeFilters],
  );

  const toggleFilter = useCallback((filter: FilterType) => {
    setActiveFilters((prev) =>
      prev.includes(filter) ? prev.filter((f) => f !== filter) : [...prev, filter],
    );
  }, []);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchValue("");
  }, []);

  const defaultRollupLevel = useMemo(
    () => getDefaultRollupLevelValue(rollupLevelOptions),
    [rollupLevelOptions],
  );

  const handleInsert = useCallback(
    (selection: Selection) => {
      // Suppress double-taps while the "Inserted" flash is still on screen;
      // once the timer expires the same row can be inserted again.
      if (recentlyAdded.has(selection.key)) return;
      const row = synthesizeQueryFieldRow(
        selection,
        nextSortOrder,
        defaultRollupLevel,
        rollupLevelOptions,
      );
      onAddField(row);
      flashAdded(selection.key);
    },
    [
      defaultRollupLevel,
      flashAdded,
      nextSortOrder,
      onAddField,
      recentlyAdded,
      rollupLevelOptions,
    ],
  );

  const handleInsertNode = useCallback(
    (node: FlattenedHierarchyNode) => {
      if (node.typeHierarchyNodeId === null) return;
      handleInsert({
        kind: "node",
        key: node.key,
        name: node.name,
        path: node.path,
        typeHierarchyNodeId: node.typeHierarchyNodeId,
      });
    },
    [handleInsert],
  );

  const handleInsertFormula = useCallback(
    (formula: Formula) =>
      handleInsert({
        kind: "formula",
        key: `formula:${formula.id}` as SelectionKey,
        name: formula.name,
        formulaId: Number(formula.id),
      }),
    [handleInsert],
  );

  const handleInsertPrimary = useCallback(
    (field: PrimaryFieldRow) =>
      handleInsert({
        kind: "primary",
        key: field.key,
        name: field.name,
        primaryField: field.primaryField,
        attributeType: field.type,
      }),
    [handleInsert],
  );

  const handleInsertDynamicDate = useCallback(
    (row: DynamicDateRow) =>
      handleInsert({
        kind: "dynamicDate",
        key: row.key,
        name: row.name,
        dynamicDate: row.dynamicDate,
      }),
    [handleInsert],
  );

  const hierarchyContext = useMemo<InsertContext<FlattenedHierarchyNode>>(
    () => ({
      disabledKeys: recentlyAdded,
      onInsert: handleInsertNode,
      rowKey: (n) => n.key,
      testIdPrefix: "picker-hierarchy",
      searchValue: debouncedSearch,
      canInsert: (n) => n.typeHierarchyNodeId !== null,
    }),
    [recentlyAdded, handleInsertNode, debouncedSearch],
  );

  const formulaContext = useMemo<InsertContext<Formula>>(
    () => ({
      disabledKeys: recentlyAdded,
      onInsert: handleInsertFormula,
      rowKey: (f) => `formula:${f.id}`,
      testIdPrefix: "picker-formula",
      searchValue: debouncedSearch,
    }),
    [recentlyAdded, handleInsertFormula, debouncedSearch],
  );

  const primaryContext = useMemo<InsertContext<PrimaryFieldRow>>(
    () => ({
      disabledKeys: recentlyAdded,
      onInsert: handleInsertPrimary,
      rowKey: (p) => p.key,
      testIdPrefix: "picker-primary",
      searchValue: debouncedSearch,
    }),
    [recentlyAdded, handleInsertPrimary, debouncedSearch],
  );

  const dynamicDateContext = useMemo<InsertContext<DynamicDateRow>>(
    () => ({
      disabledKeys: recentlyAdded,
      onInsert: handleInsertDynamicDate,
      rowKey: (row) => row.key,
      testIdPrefix: "picker-dynamic-date",
      searchValue: debouncedSearch,
    }),
    [recentlyAdded, handleInsertDynamicDate, debouncedSearch],
  );

  /** AG Grid caches cell renderers by row id, so we manually refresh
   *  the insert column when `disabledKeys` changes. */
  const hierarchyGridApiRef = useRef<AgGrid.GridApi<FlattenedHierarchyNode> | null>(null);
  const formulaGridApiRef = useRef<AgGrid.GridApi<Formula> | null>(null);
  const dynamicDateGridApiRef = useRef<AgGrid.GridApi<DynamicDateRow> | null>(null);
  const { gridApiRef: primaryGridApiRef, onGridReady: handlePrimaryGridReady } =
    usePrimaryFieldsGridAttachment<PrimaryFieldRow>(primaryFieldsDatasource);

  useEffect(() => {
    hierarchyGridApiRef.current?.refreshCells({ columns: ["insert"], force: true });
    formulaGridApiRef.current?.refreshCells({ columns: ["insert"], force: true });
    dynamicDateGridApiRef.current?.refreshCells({ columns: ["insert"], force: true });
    primaryGridApiRef.current?.refreshCells({ columns: ["insert"], force: true });
  }, [recentlyAdded, primaryGridApiRef]);

  /** Refresh text cells when the search needle changes so cached
   *  renderers pick up updated highlights. */
  useEffect(() => {
    hierarchyGridApiRef.current?.refreshCells({
      columns: ["ag-Grid-AutoColumn", "description"],
      force: true,
    });
    formulaGridApiRef.current?.refreshCells({
      columns: ["name", "description", "formula"],
      force: true,
    });
    dynamicDateGridApiRef.current?.refreshCells({
      columns: ["name"],
      force: true,
    });
    primaryGridApiRef.current?.refreshCells({
      columns: ["name", "primaryField"],
      force: true,
    });
  }, [needle, primaryGridApiRef]);

  const handleHierarchyGridReady = useCallback(
    (event: AgGrid.GridReadyEvent<FlattenedHierarchyNode>) => {
      hierarchyGridApiRef.current = event.api;
    },
    [],
  );
  const handleFormulaGridReady = useCallback((event: AgGrid.GridReadyEvent<Formula>) => {
    formulaGridApiRef.current = event.api;
  }, []);
  const handleDynamicDateGridReady = useCallback(
    (event: AgGrid.GridReadyEvent<DynamicDateRow>) => {
      dynamicDateGridApiRef.current = event.api;
    },
    [],
  );

  const hierarchyAutoGroupColumnDef = useMemo<AgGrid.ColDef<FlattenedHierarchyNode>>(
    () => ({
      headerName: "Node name",
      field: "name",
      sortable: false,
      flex: 1,
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: (params: AgGrid.ICellRendererParams<FlattenedHierarchyNode>) => {
          const search = (params.context as { searchValue?: string })?.searchValue ?? "";
          const value = params.data?.name ?? "";
          return <HighlightText text={value} searchValue={search} />;
        },
      },
    }),
    [],
  );

  const getHierarchyDataPath = useCallback(
    (data: FlattenedHierarchyNode): string[] => data.path,
    [],
  );

  const { gridOptions: hierarchyGridOptions } = useAgGridDefaults({
    overrides: FIELD_GRID_OVERRIDES,
  });
  const { gridOptions: formulaGridOptions } = useAgGridDefaults({
    overrides: FIELD_GRID_OVERRIDES,
  });
  const { gridOptions: dynamicDateGridOptions } = useAgGridDefaults({
    overrides: FIELD_GRID_OVERRIDES,
  });
  const { gridOptions: primaryGridOptions } = useAgGridDefaults({
    overrides: {
      ...FIELD_GRID_OVERRIDES,
      rowModelType: "serverSide",
      pagination: true,
      paginationPageSize: PRIMARY_FIELDS_PAGE_SIZE,
      paginationPageSizeSelector: false,
      cacheBlockSize: PRIMARY_FIELDS_PAGE_SIZE,
      suppressServerSideFullWidthLoadingRow: true,
    },
  });

  const hierarchyGroupDefaultExpanded = useMemo(() => (needle ? -1 : 1), [needle]);

  const isSearchActive = needle.length > 0;
  const primaryFieldsSearchLoaded =
    primaryFieldsLoadState.search === primaryFieldSearch &&
    primaryFieldsLoadState.hasLoaded &&
    !primaryFieldsLoadState.isLoading;
  const primaryFieldsLoadFailed = primaryFieldsSearchLoaded && primaryFieldsLoadState.hasError;
  const primaryFieldsHaveNoRows =
    primaryFieldsSearchLoaded &&
    !primaryFieldsLoadState.hasError &&
    primaryFieldsLoadState.rowCount === 0;
  const hasPrimaryFieldSearchResults =
    !showSection("primaryFields") ||
    !primaryFieldsSearchLoaded ||
    primaryFieldsLoadFailed ||
    primaryFieldsLoadState.rowCount > 0;
  const hasNoSearchResults =
    isSearchActive &&
    (!showSection("hierarchy") || filteredHierarchy.length === 0) &&
    (!showSection("formulas") || filteredFormulas.length === 0) &&
    (!showSection("dynamicFields") || filteredDynamicDates.length === 0) &&
    !hasPrimaryFieldSearchResults;

  return (
    <>
      <SlideoutDrawerV2.ComplexHeader
        title="Insert fields"
        closeButton={
          <Button
            variant="link"
            icon="arrow-right"
            iconPosition="right"
            data-testid="picker-close-btn"
          >
            Close
          </Button>
        }
        showGap={false}
        showBorder={false}
      />
      <SlideoutDrawerV2.Body className={styles.body} data-testid="add-field-picker-slideout">
        <Flex flexDirection="column" gap={SPACING.X_LARGE}>
          <Flex alignItems="center" gap={SPACING.MEDIUM}>
            <TextInput
              value={searchValue}
              onChange={handleSearchChange}
              icon="search"
              isClearable
              onClearValue={handleClearSearch}
              placeholder="Search available fields"
              data-testid="picker-search"
            />
            <DropdownButton
              label={computeFilterLabel(activeFilters)}
              keepOpen
              variant="neutral"
              icon="filter"
              iconPosition="left"
              data-testid="picker-filter"
            >
              {FILTER_OPTIONS.map((option) => (
                <DropdownButton.Action
                  key={option.value}
                  onSelect={() => toggleFilter(option.value)}
                >
                  <Checkbox
                    checked={activeFilters.includes(option.value)}
                    onChange={() => {
                      /* handled by onSelect */
                    }}
                    label={option.label}
                  />
                </DropdownButton.Action>
              ))}
            </DropdownButton>
          </Flex>

          {hasNoSearchResults ? (
            <Flex
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap={SPACING.X_SMALL}
              className={styles.noResultsContainer}
              data-testid="picker-no-results"
            >
              <Icon type="search" size="xxx-large" color={Colors.Neutral600} />
              <Text variant="h4">No results found</Text>
              <Text variant="regular-sm" align="center">
                No match within the hierarchy, formulas, dynamic fields, or primary fields. Try
                a different search term.
              </Text>
            </Flex>
          ) : (
            <>
              {showSection("hierarchy") && (
                <Flex
                  flexDirection="column"
                  gap={SPACING.MEDIUM}
                  data-testid="picker-section-hierarchy"
                >
                  <Text variant="h4">Hierarchy</Text>
                  {filteredHierarchy.length > 0 ? (
                    <Grid<FlattenedHierarchyNode>
                      rowData={filteredHierarchy}
                      getRowId={(params) => params.data?.id ?? ""}
                      columnDefs={HIERARCHY_COLUMN_DEFS}
                      context={hierarchyContext}
                      onGridReady={handleHierarchyGridReady}
                      treeData
                      getDataPath={getHierarchyDataPath}
                      autoGroupColumnDef={hierarchyAutoGroupColumnDef}
                      groupDefaultExpanded={hierarchyGroupDefaultExpanded}
                      gridOptions={hierarchyGridOptions}
                    />
                  ) : (
                    <Text variant="secondary-sm">No hierarchy nodes available</Text>
                  )}
                </Flex>
              )}

              {showSection("formulas") && (
                <Flex
                  flexDirection="column"
                  gap={SPACING.MEDIUM}
                  data-testid="picker-section-formulas"
                >
                  <Text variant="h4">Formulas</Text>
                  {filteredFormulas.length > 0 ? (
                    <Grid<Formula>
                      rowData={filteredFormulas}
                      getRowId={(params) => params.data?.id ?? ""}
                      columnDefs={FORMULA_COLUMN_DEFS}
                      context={formulaContext}
                      onGridReady={handleFormulaGridReady}
                      gridOptions={formulaGridOptions}
                    />
                  ) : (
                    <Text variant="secondary-sm">No formulas available</Text>
                  )}
                </Flex>
              )}

              {showSection("dynamicFields") && (
                <Flex
                  flexDirection="column"
                  gap={SPACING.MEDIUM}
                  data-testid="picker-section-dynamicFields"
                >
                  <Text variant="h4">Dynamic fields</Text>
                  {filteredDynamicDates.length > 0 ? (
                    <Grid<DynamicDateRow>
                      rowData={[...filteredDynamicDates]}
                      getRowId={(params) => params.data?.id ?? ""}
                      columnDefs={DYNAMIC_DATE_COLUMN_DEFS}
                      context={dynamicDateContext}
                      onGridReady={handleDynamicDateGridReady}
                      gridOptions={dynamicDateGridOptions}
                    />
                  ) : (
                    <Text variant="secondary-sm">No dynamic fields match your search</Text>
                  )}
                </Flex>
              )}

              {showSection("primaryFields") && (
                <Flex
                  flexDirection="column"
                  gap={SPACING.MEDIUM}
                  data-testid="picker-section-primaryFields"
                >
                  <Text variant="h4">Primary fields</Text>
                  {primaryFieldsLoadFailed ? (
                    <Text variant="secondary-sm">Unable to load primary fields</Text>
                  ) : primaryFieldsHaveNoRows ? (
                    <Text variant="secondary-sm">No primary fields available</Text>
                  ) : (
                    <Grid<PrimaryFieldRow>
                      getRowId={(params) => params.data?.id ?? ""}
                      columnDefs={PRIMARY_FIELD_COLUMN_DEFS}
                      context={primaryContext}
                      onGridReady={handlePrimaryGridReady}
                      gridOptions={primaryGridOptions}
                    />
                  )}
                </Flex>
              )}
            </>
          )}
        </Flex>
      </SlideoutDrawerV2.Body>
    </>
  );
};

/** Keeps the slideout mounted for enter/exit animations; mounts the
 *  stateful content only while open. */
export const AddFieldPickerSlideout = ({
  isOpen,
  onClose,
  ...contentProps
}: AddFieldPickerSlideoutProps) => (
  <SlideoutDrawerV2
    isOpen={isOpen}
    onClose={onClose}
    isDismissable
    overlayStyle={OverlayStyle.Transparent}
    zIndex={Z_INDEX.drawer}
    data-testid="add-field-picker-drawer"
  >
    {isOpen ? <AddFieldPickerContent {...contentProps} /> : null}
  </SlideoutDrawerV2>
);
